import { stepKycOrders } from "./stepKycOrders";

export type KycType = "individual" | "non_individual" | "joint_individual";

// Individual KYC step titles
export const INDIVIDUAL_STEPS_TITLES: Array<{ label: string; value: string }> =
  [
    { label: "Upload Documents", value: "offline_kyc" },
    { label: "EMD & Bid details", value: "bid_details" },
    { label: "Terms & Conditions", value: "t_&_c" },
    { label: "Bid Application Download", value: "bid_application_download" },
    { label: "KYC Approver Details", value: "approver_details" },
  ];

// Non-individual KYC step titles
export const NON_INDIVIDUAL_STEPS_TITLES_ONLINE: Array<{
  label: string;
  value: string;
}> = [
  { label: "Business Details", value: "business_details" },
  { label: "Assign Representative", value: "add_ar" },
  { label: "EMD & Bid details", value: "bid_details" },
  { label: "Declaration", value: "declaration_of_ar" },
  { label: "Terms & Conditions", value: "t_and_c" },
  { label: "E-Sign", value: "e-sign" },
];

export const NON_INDIVIDUAL_STEPS_TITLES_OFFLINE: Array<{
  label: string;
  value: string;
}> = [
  { label: "Business Details", value: "business_details" },
  { label: "Assign Representative", value: "add_ar" },
  { label: "EMD & Bid details", value: "bid_details" },
  { label: "Declaration", value: "declaration_of_ar" },
  { label: "Terms & Conditions", value: "t_and_c" },
  { label: "Bid Application Download", value: "bid_application_download" },
  { label: "KYC Approver Details", value: "approver_details" },
];

// Joint individual KYC step titles
export const JOINT_INDIVIDUAL_STEPS_TITLES_PRIMARY: Array<{
  label: string;
  value: string;
}> = [
  { label: "Fetch Documents", value: "online_kyc" },
  { label: "Participation Proof", value: "joint_proof" },
  { label: "EMD & Bid details", value: "bid_details" },
  { label: "Terms & Conditions", value: "t_&_c" },
  { label: "E-Sign", value: "e-sign" },
];

export const JOINT_INDIVIDUAL_STEPS_TITLES_VIEWER: Array<{
  label: string;
  value: string;
}> = [
  { label: "Fetch Documents", value: "online_kyc" },
  { label: "Terms & Conditions", value: "t_&_c" },
  { label: "Participation Consent", value: "e-sign" },
];

export const JOINT_INDIVIDUAL_STEPS_TITLES_PRIMARY_OFFLINE: Array<{
  label: string;
  value: string;
}> = [
  { label: "Fetch Documents", value: "online_kyc" },
  { label: "Participation Proof", value: "joint_proof" },
  { label: "EMD & Bid details", value: "bid_details" },
  { label: "Terms & Conditions", value: "t_&_c" },
  { label: "Bid Application Download", value: "bid_application_download" },
  { label: "KYC Approver Details", value: "approver_details" },
];

export const JOINT_INDIVIDUAL_STEPS_TITLES_VIEWER_OFFLINE: Array<{
  label: string;
  value: string;
}> = [
  { label: "Fetch Documents", value: "online_kyc" },
  { label: "Terms & Conditions", value: "t_&_c" },
  // { label: "Bid Application Download", value: "bid_application_download" },
  { label: "KYC Approver Details", value: "approver_details" },
];

// Legacy export for backward compatibility
export const STEPS_TITLES = INDIVIDUAL_STEPS_TITLES;

// Helper function to get step titles based on KYC type, mode, and access type
export const getStepTitles = (
  kycType: KycType,
  kycMode: string,
  accessType?: string,
): Array<{ label: string; value: string }> => {
  if (kycType === "non_individual") {
    return kycMode === "offline"
      ? NON_INDIVIDUAL_STEPS_TITLES_OFFLINE
      : NON_INDIVIDUAL_STEPS_TITLES_ONLINE;
  }
  if (kycType === "joint_individual") {
    if (kycMode === "offline") {
      return accessType === "viewer"
        ? JOINT_INDIVIDUAL_STEPS_TITLES_VIEWER_OFFLINE
        : JOINT_INDIVIDUAL_STEPS_TITLES_PRIMARY_OFFLINE;
    } else {
      return accessType === "viewer"
        ? JOINT_INDIVIDUAL_STEPS_TITLES_VIEWER
        : JOINT_INDIVIDUAL_STEPS_TITLES_PRIMARY;
    }
  }
  // For individual, return individual steps
  return INDIVIDUAL_STEPS_TITLES;
};

export const STEP_TO_INDEX_MAP: Record<string, number> = {
  offline_kyc: 0,
  bid_details: 1,
  "t_&_c": 2,
  bid_application_download: 3,
  approver_details: 4,
  completed: 5,
};

export const getStepOrder = (
  kycType: string,
  kycMode: string,
  accessType?: string,
): string[] => {
  const stepOrders = stepKycOrders as Record<string, string[]>;
  const effectiveAccessType = accessType || "primary";
  let key: string;

  if (kycType === "joint_individual") {
    if (kycMode === "offline") {
      key = `${kycType}_${effectiveAccessType}_offline`;
    } else {
      key = `${kycType}_${effectiveAccessType}`;
    }
  } else if (kycType === "individual" && kycMode === "online") {
    key = `${kycType}_${kycMode}`;
  } else {
    key = `${kycType}_${kycMode}`;
  }

  return stepOrders[key] || [];
};

export const findFirstPendingStep = (
  kycSavedSteps: any,
  kycType: string,
  kycMode: string,
  accessType?: string,
): string | null => {
  if (!kycSavedSteps || typeof kycSavedSteps !== "object") {
    return null;
  }

  const stepOrder = getStepOrder(kycType, kycMode, accessType);
  if (!stepOrder || stepOrder.length === 0) {
    return null;
  }

  // First, check for rejected steps
  for (const step of stepOrder) {
    if (kycSavedSteps[step] === "rejected") {
      return step;
    }
  }

  // Then, check for pending or incomplete steps
  for (const step of stepOrder) {
    const status = kycSavedSteps[step];
    if (!status || status === "pending" || status === "rejected") {
      return step;
    }
  }

  // Return the last step if all are completed
  return stepOrder[stepOrder.length - 1];
};

export const determineKycType = (bidderData: any): KycType => {
  if (bidderData?.is_joint === true) {
    return "joint_individual";
  }

  const userType = bidderData?.meta?.user_details?.user_type;
  if (typeof userType === "object") {
    return userType.value === "individual" ? "individual" : "non_individual";
  }

  return userType === "individual" ? "individual" : "non_individual";
};

export const getStepIndex = (step: string | null): number => {
  if (!step) return 0;
  return STEP_TO_INDEX_MAP[step] ?? 0;
};

// Helper function to get step index for non-individual flows
export const getNonIndividualStepIndex = (
  step: string | null,
  kycMode: string,
): number => {
  if (!step) return 0;

  if (kycMode === "offline") {
    const offlineMap: Record<string, number> = {
      business_details: 0,
      add_ar: 1,
      bid_details: 2,
      declaration_of_ar: 3,
      ar_declaration: 3,
      t_and_c: 4,
      "t_&_c": 4,
      bid_application_download: 5,
      approver_details: 6,
      completed: 7,
    };
    return offlineMap[step] ?? 0;
  } else {
    const onlineMap: Record<string, number> = {
      business_details: 0,
      add_ar: 1,
      bid_details: 2,
      declaration_of_ar: 3,
      ar_declaration: 3,
      t_and_c: 4,
      "t_&_c": 4,
      "e-sign": 5,
      e_sign: 5,
      completed: 6,
    };
    return onlineMap[step] ?? 0;
  }
};

// Helper function to get step index for joint individual flows
export const getJointIndividualStepIndex = (
  step: string | null,
  accessType?: string,
  kycMode?: string,
): number => {
  if (!step) return 0;

  const effectiveAccessType = accessType || "primary";
  const effectiveKycMode = kycMode || "online";

  if (effectiveKycMode === "offline") {
    if (effectiveAccessType === "viewer") {
      const viewerOfflineMap: Record<string, number> = {
        offline_kyc: 0,
        "t_&_c": 1,
        approver_details: 3,
        completed: 4,
      };
      return viewerOfflineMap[step] ?? 0;
    } else {
      // Primary offline
      const primaryOfflineMap: Record<string, number> = {
        online_kyc: 0,
        joint_proof: 1,
        bid_details: 2,
        "t_&_c": 3,
        bid_application_download: 4,
        approver_details: 5,
        wait_for_consent: 6,
        completed: 6,
      };
      return primaryOfflineMap[step] ?? 0;
    }
  } else {
    // Online mode
    if (effectiveAccessType === "viewer") {
      const viewerMap: Record<string, number> = {
        online_kyc: 0,
        "t_&_c": 1,
        "e-sign": 2,
        e_sign: 2,
        completed: 3,
      };
      return viewerMap[step] ?? 0;
    } else {
      // Primary access type
      const primaryMap: Record<string, number> = {
        online_kyc: 0,
        joint_proof: 1,
        bid_details: 2,
        "t_&_c": 3,
        "e-sign": 4,
        e_sign: 4,
        wait_for_consent: 5,
        completed: 5,
      };
      return primaryMap[step] ?? 0;
    }
  }
};
